import type { Me, Overview, ProjectOverview } from '@ft/shared';
import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ExportButton } from '../components/ExportButton';
import { SavedViews } from '../components/SavedViews';
import { Cust, Loading, useLoad } from '../components/ui';
import { useI18n, type T } from '../i18n';
import { useRememberedFilter } from '../lib/remember';
import { getAreaName } from '../lib/area';
import { custColor, eur, pct } from '../lib/format';

const TONE = { noTarget: 'neu', uncovered: 'neg', behind: 'crit', onTrack: 'pos' } as const;

/** Fortschrittsbalken: Rahmen = bestellt, Füllung = abgerechnet, Strich = Soll heute. Alles relativ zum größten Wert. */
export function Track({ p, pace, t }: { p: ProjectOverview; pace: number; t: T }) {
  const scale = Math.max(p.targetCents ?? 0, p.orderedCents, p.billedCents) || 1;
  const w = (v: number) => `${Math.min(100, (v / scale) * 100)}%`;
  return (
    <div className="track" role="img" aria-label={t('pz.trackLabel', { project: p.name, billed: eur(p.billedCents), ordered: eur(p.orderedCents), target: p.targetCents !== null ? eur(p.targetCents) : '–' })}>
      <div className="ord" style={{ width: w(p.orderedCents) }} />
      <div className="inv" style={{ width: w(p.billedCents) }} />
      {p.targetCents !== null && <div className="tgt" style={{ left: w(p.targetCents) }} />}
      {p.targetCents !== null && <div className="pace" style={{ left: w(p.targetCents * pace) }} />}
    </div>
  );
}

export function HealthPill({ h, t }: { h: ProjectOverview['health']; t: T }) {
  return <span className={`pill ${TONE[h]}`}>{t(`pz.health.${h}`)}</span>;
}

export function ProjectsPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  useRememberedFilter('projects');
  const thisYear = new Date().getFullYear();
  const year = Number(sp.get('jahr') ?? thisYear);
  const { data, error } = useLoad<Overview>(`/api/overview?year=${year}`);
  const set = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const fc = sp.get('kunde') ?? '';
  const fpm = sp.get('pm') ?? '';
  const fh = sp.get('status') ?? '';

  const customers = useMemo(() => {
    const m = new Map<string, { target: number; billed: number }>();
    for (const p of data?.projects ?? []) {
      const x = m.get(p.customer.name) ?? { target: 0, billed: 0 };
      m.set(p.customer.name, { target: x.target + (p.targetCents ?? 0), billed: x.billed + p.billedCents });
    }
    return [...m].sort((a, b) => b[1].target - a[1].target);
  }, [data]);

  const pms = useMemo(() => [...new Map((data?.projects ?? []).flatMap((p) => [p.pm, p.deputy]).filter((x): x is NonNullable<typeof x> => !!x).map((u) => [u.id, u])).values()].sort((a, b) => a.name.localeCompare(b.name)), [data]);

  const list = (data?.projects ?? [])
    .filter((p) => (!fc || p.customer.name === fc) && (!fh || p.health === fh) &&
      (!fpm || (fpm === 'none' ? !p.pm : p.pm?.id === fpm || p.deputy?.id === fpm)))
    .sort((a, b) => Number(a.empty) - Number(b.empty) || customers.findIndex((c) => c[0] === a.customer.name) - customers.findIndex((c) => c[0] === b.customer.name) || (b.targetCents ?? 0) - (a.targetCents ?? 0));
  const sum = (k: 'targetCents' | 'orderedCents' | 'billedCents' | 'openCents') => list.reduce((s, p) => s + (p[k] ?? 0), 0);
  const pace = data?.pace ?? 0;

  return (
    <section className="page">
      <div className="pagehead">
        <div><div className="crumb">{t('crumb.root', { area: getAreaName() })}</div><h1>{t('pz.title')}</h1></div>
        <span className="spacer" />
        <SavedViews />
        <ExportButton year={year} />
        <div className="fld">
          <label htmlFor="pz-y">{t('pz.year')}</label>
          <select id="pz-y" className="yearsel" value={year} onChange={(e) => set('jahr', e.target.value === String(thisYear) ? '' : e.target.value)}>
            {[thisYear - 1, thisYear, thisYear + 1].map((y) => <option key={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {!data ? <Loading error={error} /> : (
        <>
          <div className="custbars">
            {customers.map(([name, x]) => {
              const u = x.target ? x.billed / x.target : 0;
              return (
                <button key={name} className="cbar" aria-pressed={fc === name} onClick={() => set('kunde', fc === name ? '' : name)}>
                  <span className="n"><i className="sw" style={{ background: custColor(name) }} />{name}</span>
                  <div className="meter"><i style={{ width: `${Math.min(100, u * 100)}%` }} />{x.target > 0 && <span className="pc" style={{ left: `${pace * 100}%` }} />}</div>
                  <span className="f num">{x.target ? t('pz.custOf', { billed: eur(x.billed), target: eur(x.target), p: pct(u) }) : eur(x.billed)}</span>
                </button>
              );
            })}
          </div>

          <div className="chips" role="group" aria-label={t('common.pm')}>
            <button className="chip" aria-pressed={!fpm} onClick={() => set('pm', '')}>{t('pz.allPms')}</button>
            {pms.map((u) => (
              <button key={u.id} className="chip" aria-pressed={fpm === u.id} onClick={() => set('pm', fpm === u.id ? '' : u.id)}>
                <span className="pmc">{u.initials}</span>{u.name}{u.id === me.id ? ` ${t('pz.me')}` : ''}
              </button>
            ))}
            <button className="chip" aria-pressed={fpm === 'none'} onClick={() => set('pm', fpm === 'none' ? '' : 'none')}>{t('common.withoutPm')}</button>
            <span className="spacer" />
            <select aria-label={t('common.status')} className="chipsel" value={fh} onChange={(e) => set('status', e.target.value)}>
              <option value="">{t('common.status')}: {t('common.all')}</option>
              {(['onTrack', 'behind', 'uncovered', 'noTarget'] as const).map((h) => <option key={h} value={h}>{t(`pz.health.${h}`)}</option>)}
            </select>
          </div>

          <div className="box">
            <div className="panel-h">
              <h2>{fc ? `${fc} · ` : ''}{t('pz.count', { n: list.length })}</h2>
              {(fc || fpm || fh) && <button className="btn ghost" onClick={() => setSp(year === thisYear ? new URLSearchParams() : new URLSearchParams({ jahr: String(year) }), { replace: true })}>{t('pz.removeFilter')}</button>}
              <span className="spacer" />
              <div className="legend">
                <span><i className="lg" style={{ background: 'var(--neu-bar)' }} />{t('pz.legend.billed')}</span>
                <span><i className="lg" style={{ border: '1.5px solid var(--neu-bar)' }} />{t('pz.legend.ordered')}</span>
                <span><i className="lg pacekey" />{t('pz.legend.pace', { p: pct(pace) })}</span>
              </div>
            </div>
            <div className="tablewrap">
              {!list.length ? <div className="empty">{t('pz.empty')}</div> : (
                <table>
                  <thead><tr>
                    <th>{t('common.project')}</th><th>{t('common.customer')}</th><th>{t('ct.col.contract')}</th><th>{t('common.pm')}</th>
                    <th className="r">{t('pz.col.target')}</th><th className="r">{t('pz.col.ordered')}</th><th className="r">{t('pz.col.billed')}</th><th className="r">{t('pz.col.open')}</th>
                    <th style={{ minWidth: 180 }}>{t('pz.col.progress')}</th><th className="r">{t('pz.col.reach')}</th><th>{t('common.status')}</th>
                  </tr></thead>
                  <tbody>
                    {list.map((p) => (
                      <tr key={p.id} className={`click${p.empty ? ' emptyrow' : ''}`} tabIndex={0} onClick={() => nav(`/bestellungen?projekt=${encodeURIComponent(p.name)}`)} onKeyDown={(e) => { if (e.key === 'Enter') nav(`/bestellungen?projekt=${encodeURIComponent(p.name)}`); }}>
                        <td><b style={{ fontWeight: 600 }}>{p.name}</b>{p.domain && <div className="muted small">{p.domain}</div>}{p.empty && <div className="muted small"><i>{t('pz.emptyHint')}</i></div>}</td>
                        <td><Cust name={p.customer.name} /></td>
                        <td className="small">{p.contract
                          ? <><Link style={{ color: "var(--accent)", textDecoration: "none" }} to={`/vertraege/${p.contract.id}`} onClick={(e) => e.stopPropagation()}>{p.contract.name}</Link>{!p.contract.filed && <span className="pill crit" style={{ marginLeft: 4 }} title={t('ct.notFiledHint')}>{t('ct.notFiled')}</span>}</>
                          : <span className="muted">{t('pz.noContract')}</span>}</td>
                        <td>{p.pm ? <span className="pmc" title={p.pm.name}>{p.pm.initials}</span> : <span className="muted">–</span>}</td>
                        <td className="r num">{p.targetCents !== null ? eur(p.targetCents) : '–'}</td>
                        <td className="r num">{eur(p.orderedCents)}</td>
                        <td className="r num">{eur(p.billedCents)}</td>
                        <td className="r num">{eur(p.openCents)}</td>
                        <td><Track p={p} pace={pace} t={t} /></td>
                        <td className="r num">{p.targetCents ? pct(p.billedCents / p.targetCents) : '–'}</td>
                        <td><HealthPill h={p.health} t={t} /></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr>
                    <td colSpan={4}>{t('common.sum')}</td>
                    <td className="r num">{eur(sum('targetCents'))}</td><td className="r num">{eur(sum('orderedCents'))}</td><td className="r num">{eur(sum('billedCents'))}</td><td className="r num">{eur(sum('openCents'))}</td>
                    <td /><td className="r num">{sum('targetCents') ? pct(sum('billedCents') / sum('targetCents')) : '–'}</td><td />
                  </tr></tfoot>
                </table>
              )}
            </div>
          </div>
          <p className="muted small" style={{ margin: 0 }}>{t('pz.healthHelp')}</p>
        </>
      )}
    </section>
  );
}
